import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import useReveal from "@/hooks/useReveal";
import QuestionCard from "@/components/site/QuestionCard";

const ACCENT = "#F57F17"; // gold

const QUESTIONS = [
  {
    q: "Can a 16-year-old explain why ₹500 a month saved at 17 becomes ₹78 lakh by 60 — but only ₹24 lakh if they wait until 27?",
    f: "That ₹54 lakh difference is just ten years of delay. Compound interest is the single most powerful idea in personal finance, and it appears on no school syllabus.",
  },
  {
    q: "Does a teenager know what happens in their brain in the six seconds after they feel anger — and why those six seconds decide the outcome?",
    f: "The emotional brain overrides the rational one for about six seconds. A trained pause is the difference between a reaction regretted and a decision made well.",
  },
  {
    q: "Does a young person know the difference between what they're good at and what they actually value — and that confusing the two is the most common career mistake?",
    f: "Most pick a path on what they're good at or what's expected, then spend years drained by it. A simple values exercise prevents a decade of misalignment.",
  },
  {
    q: "Can a 16-year-old send a message to a stranger they admire that actually gets a reply?",
    f: "Most send generic, forgettable notes. A specific, well-prepared message has opened doors that years of waiting never could — including mentors and first jobs.",
  },
  {
    q: "Does a teenager understand why most professionals can't beat a simple index fund — and how to start one in twenty minutes?",
    f: "Over a decade, the large majority of expert-managed funds underperform the index. The cheaper, simpler option is usually the smarter one — and almost no one is told this.",
  },
  {
    q: "Does a young person know that what predicts a lasting relationship is not the absence of conflict, but how well two people repair after it?",
    f: "Decades of research show contempt — not disagreement — is what ends relationships. This is learnable, and it is taught to almost no one.",
  },
  {
    q: "Can a 16-year-old read a payslip, a simple balance sheet, or work out their own family's net worth?",
    f: "Most adults have never added it up. One family doing this discovered lakhs in assets they hadn't realised they held.",
  },
];

const AREAS = [
  { tag: "A", t: "Mental Strength", d: "Emotions, resilience, focus and deep work, self-awareness, clear decision-making." },
  { tag: "B", t: "Leadership & Communication", d: "Leading without authority, speaking and writing well, negotiation, ethics, mentors." },
  { tag: "C", t: "Money & Investing", d: "How wealth is built, equity and index funds, protecting earnings, reading financial statements, credit." },
  { tag: "D", t: "Business & Career", d: "Designing a career, entrepreneurship, contracts, public speaking, productivity." },
  { tag: "E", t: "Life Design & Legacy", d: "Purpose, health that lasts, relationships, and the life worth building." },
];

export default function Level3() {
  useReveal();
  useEffect(() => { document.title = "Level 3 — Life Design · LifeIQ"; }, []);

  return (
    <main>
      {/* HERO */}
      <section
        className="bg-[#0D1B2A] text-white relative"
        style={{
          backgroundImage:
            "linear-gradient(rgba(13,27,42,0.95) 0%, rgba(13,27,42,0.97) 100%), url('https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1920&q=60')",
          backgroundSize: "cover",
          backgroundPosition: "center right",
        }}
      >
        <div className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ backgroundColor: ACCENT }} />
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 pt-20 md:pt-28 pb-24 md:pb-28">
          <div className="max-w-4xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>
              Level 3 · Life Intelligence · Age 14–16
            </div>
            <h1 className="mt-5 font-serif-d text-[42px] sm:text-6xl lg:text-[66px] leading-[1.05] tracking-tight">
              Can a young adult lead their own life — instead of drifting into one?
            </h1>
            <p className="mt-8 text-[17px] sm:text-[19px] text-white/85 leading-[1.7] max-w-3xl">
              By 16, most decisions that shape a life are about to be made: what to value, how to handle pressure, how money grows, how to lead, what to build. Level 3 gives a 14 to 16-year-old the frameworks to make those decisions deliberately — not by accident or by default.
            </p>
            <p className="mt-6 font-serif-d italic text-[17px] sm:text-[19px] leading-[1.5] max-w-3xl" style={{ color: "#F57F17" }}>
              Life Intelligence — closing the gap between a young adult and life's major decisions.
            </p>
          </div>
        </div>
      </section>

      {/* DISCOVERY QUESTIONS */}
      <section className="bg-white">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-3xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>Before we explain Level 3 — try these.</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08]">
              The questions that decide an adult life.
            </h2>
            <p className="mt-6 text-[17px] sm:text-[19px] text-[#5C6470] leading-[1.7]">
              These are the questions that quietly decide the shape of an adult life. Notice how rarely anyone is actually taught the answers.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {QUESTIONS.map((c, i) => <QuestionCard key={i} q={c.q} f={c.f} i={i} accent={ACCENT} />)}
          </div>

          <div className="mt-16 max-w-3xl reveal">
            <p className="font-serif-d text-[26px] sm:text-[32px] leading-[1.3]" style={{ color: ACCENT }}>
              None of these are about intelligence. They are specific, learnable frameworks — and learned early, they compound for a lifetime. That is Level 3.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT LEVEL 3 COVERS */}
      <section className="bg-[#F5F5F4]">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-3xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>What Level 3 covers</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08]">
              Five areas. One outcome: a young adult who leads their life on purpose.
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {AREAS.map((a, i) => (
              <div key={a.tag} data-testid={`area-${a.tag}`} className="reveal bg-white border border-[#E5E7EA] rounded-sm p-7 flex flex-col" style={{ transitionDelay: `${i * 60}ms` }}>
                <div className="h-1 w-10" style={{ backgroundColor: ACCENT }} />
                <div className="mt-5 text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>Area {a.tag}</div>
                <h3 className="mt-2 font-serif-d text-[22px] text-[#0D1B2A] leading-tight">{a.t}</h3>
                <p className="mt-3 text-[15px] text-[#5C6470] leading-[1.65]">{a.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OUTCOME */}
      <section className="bg-[#0D1B2A] text-white">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-3xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#F57F17] font-semibold">By the end of Level 3</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08] text-white">
              Adulthood, entered on purpose.
            </h2>
            <p className="mt-7 text-[17px] sm:text-[19px] text-white/85 leading-[1.75]">
              A young adult has the mental strength to handle pressure, the financial framework to build wealth from their first income, the communication and leadership skills to move people, and a clear sense of what they actually value. They enter adulthood not hoping to figure it out — but already building, deliberately.
            </p>
            <p className="mt-10 font-serif-d italic text-[26px] sm:text-[32px] leading-[1.3] text-[#F57F17]">
              Lead your life — don't let life lead you.
            </p>
            <Link to="/contact" data-testid="level3-cta" className="mt-10 inline-flex items-center gap-2 bg-[#F57F17] text-white px-7 py-4 text-[13px] font-semibold tracking-wide uppercase hover:brightness-95 transition rounded-sm">
              Book a Pilot Conversation <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
